import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import ResponsiveAppBar from "../../components/ResponsiveAppBar";
import backgroundIcon from "../../assets/images/background_icon.svg";
import { useTheme, Button, Typography } from "@mui/material";
import OutlinedLabelledTextField from "../../components/OutlinedLabelledTextfield";
import MessageCard from "./components/MessageCard";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { checkVerify } from "../../api/checkVerify.request";
import { Link } from "react-router-dom";
import { reset } from "../../api/reset.request";

function EmailVerificationSuccessful() {
  const theme = useTheme();
  const [workMail, setWorkMail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState(""); // New state for error message
  const navigate = useNavigate();

  const handleEmailChange = (event) => {
    setWorkMail(event.target.value);
  };

  const isWorkMailValid = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(workMail);
  };

  const handleSendEmail = async () => {
    try {
      await reset({ email: workMail });
      setEmailSent(true);
      checkEmailVerification();
    } catch (error) {
      if (error.response && error.response.data.message) {
        setError("No account associated with this email");
      } else {
        setError("An error occurred. Please try again.");
      }
      console.error("Error sending reset email:", error);
    }
  };

  const checkEmailVerification = async () => {
    try {
      const interval = setInterval(async () => {
        const response = await checkVerify({ email: workMail }, true);
        if (response.verified) {
          clearInterval(interval);
          setIsVerified(true);
          navigate(`/resetPassword?email=${encodeURIComponent(workMail)}`);
        }
      }, 5000);
    } catch (error) {
      console.error("Error checking email verification:", error);
    }
  };

  useEffect(() => {
    if (isVerified) {
      navigate("/resetPassword", { state: { email: workMail } });
    }
  }, [isVerified, navigate, workMail]);

  return (
    <>
      <ResponsiveAppBar />
      <div
        style={{
          backgroundImage: `url(${backgroundIcon})`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right",
          minHeight: "65vh",
        }}
      >
        <Container
          className="h-[80vh]"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          {emailSent ? (
            <MessageCard
              loaderComponent={
                <CheckCircleIcon
                  sx={{
                    fontSize: 60,
                    mt: 4,
                    color: theme.palette.green.text,
                  }}
                />
              }
              primaryText={"Reset Password Email Sent"}
              secondaryText={
                <>
                  We've just emailed you a reset link to <br />
                  <span className="text-[#5E5E5E] ">{workMail}</span>
                  . <br />
                  Click on the link to reset your password
                </>
              }
              richText={"Entered wrong email?"}
              richLinkText={"Go Back"}
              richLinkHref={"/login"}
              onRichLinkClick={() => {
                navigate("/login");
              }}
            />
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: { xs: "90%", md: "30%" },
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{
                    mt: { xs: 7, md: 10 },
                    fontWeight: "bold",
                    fontSize: { xs: "1.6rem" },
                  }}
                >
                  Reset Password
                </Typography>
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{ mb: { md: 4 }, color: "grey.text", textAlign: 'center' }}
                >
                  Enter your email linked to your account and we'll send you a
                  link to reset your password.
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mt: 2,
                  width: "100%",
                  maxWidth: "sm",
                }}
              >
                <OutlinedLabelledTextField
                  id="workMail"
                  label="Work Email"
                  placeholder="Enter your email"
                  type="email"
                  value={workMail}
                  onChange={handleEmailChange}
                  width="65%"
                  error={!!error} // Set error state for the field
                  helperText={error} // Display error message
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{
                    mb: { xs: 1, md: 3 },
                    borderRadius: "8px",
                    mt: 5,
                    backgroundColor: isWorkMailValid()
                      ? theme.palette.primary.main
                      : theme.palette.grey.border,
                    color: isWorkMailValid() ? "white" : theme.palette.text,
                    width: { md: "67%" },
                    textTransform: "none",
                  }}
                  disabled={!isWorkMailValid()}
                  onClick={handleSendEmail} // Call handleSendEmail on click
                >
                  Send Email
                </Button>
              </Box>
            </>
          )}
        </Container>
      </div>
    </>
  );
}

export default EmailVerificationSuccessful;
